import {
	type Dispatch,
	type RefObject,
	type SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { api, type CanvasCommentRow } from "@/lib/api";
import { sameCanvasPos } from "./canvas-utils";

interface UseCanvasCommentsOptions {
	projectId: string | undefined;
	canvasRef: RefObject<HTMLDivElement | null>;
	zoom: number;
	setZoom: Dispatch<SetStateAction<number>>;
	setCanvasOffset: Dispatch<SetStateAction<{ x: number; y: number }>>;
}

/**
 * Canvas comments: comment mode, optimistic create / edit / resolve, and the
 * live WebSocket feed (`/ws/canvas/:projectId`) with auto-reconnect.
 */
export function useCanvasComments({
	projectId,
	canvasRef,
	zoom,
	setZoom,
	setCanvasOffset,
}: UseCanvasCommentsOptions) {
	const { user } = useAuth();

	// --- Comment Mode State ---
	const [isCommentMode, setIsCommentMode] = useState(false);
	const [comments, setComments] = useState<CanvasCommentRow[]>([]);
	const [pendingComment, setPendingComment] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [commentInput, setCommentInput] = useState("");
	const [activeCommentId, setActiveCommentId] = useState<number | null>(null);
	const [hoveredCommentId, setHoveredCommentId] = useState<number | null>(null);
	const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
	const [editingText, setEditingText] = useState("");
	const commentInputRef = useRef<HTMLInputElement>(null);
	const editInputRef = useRef<HTMLInputElement>(null);

	// --- Comment Mode Handlers ---
	const handleCommentToggle = useCallback(() => {
		setIsCommentMode((v) => {
			if (v) {
				// Exiting comment mode — clear pending
				setPendingComment(null);
				setCommentInput("");
				setActiveCommentId(null);
			}
			return !v;
		});
	}, []);

	// Click on the canvas in comment mode: open the input at canvas-space coords
	const placePendingComment = useCallback((x: number, y: number) => {
		setPendingComment({ x, y });
		setCommentInput("");
		setActiveCommentId(null);
		setTimeout(() => commentInputRef.current?.focus(), 50);
	}, []);

	const cancelPendingComment = useCallback(() => {
		setPendingComment(null);
		setCommentInput("");
	}, []);

	const handleCommentSubmit = useCallback(async () => {
		if (!commentInput.trim() || !pendingComment || !projectId) return;

		// Optimistic: insert a draft with a temporary negative id
		const draft: CanvasCommentRow = {
			id: -Date.now(), // negative so it never clashes with real db ids
			conversationId: projectId,
			userId: user?.id || "",
			content: commentInput.trim(),
			posX: pendingComment.x,
			posY: pendingComment.y,
			resolved: false,
			createdAt: new Date().toISOString(),
		};
		setComments((prev) => [...prev, draft]);
		setPendingComment(null);
		setCommentInput("");

		try {
			// Real API call — pg_notify may echo the row via WS before this resolves;
			// always reconcile state here so we never keep the optimistic draft + WS row.
			const created = await api.postComment({
				conversationId: projectId,
				content: draft.content,
				posX: draft.posX,
				posY: draft.posY,
			});
			setComments((prev) => {
				const withoutDraft = prev.filter((c) => c.id !== draft.id);
				if (withoutDraft.some((c) => c.id === created.id)) {
					return withoutDraft;
				}
				return [...withoutDraft, created];
			});
		} catch (err) {
			// Roll back optimistic comment on error
			setComments((prev) => prev.filter((c) => c.id !== draft.id));
			toast.error("Failed to save comment");
		}
	}, [commentInput, pendingComment, projectId, user?.id]);

	const handleDeleteComment = useCallback((commentId: number) => {
		setComments((prev) => prev.filter((c) => c.id !== commentId));
		setActiveCommentId((prev) => (prev === commentId ? null : prev));
		setEditingCommentId(null);
	}, []);

	const handleResolveComment = useCallback(async (commentId: number) => {
		try {
			await api.resolveComment(commentId);
			// Remove immediately for the user who clicked — WS will remove for all other clients
			setComments((prev) => prev.filter((c) => c.id !== commentId));
		} catch {
			toast.error("Failed to resolve comment");
		}
	}, []);

	const handleMarkerClick = useCallback((commentId: number) => {
		setActiveCommentId((prev) => (prev === commentId ? null : commentId));
		setPendingComment(null);
	}, []);

	const handleStartEdit = useCallback((comment: CanvasCommentRow) => {
		setEditingCommentId(comment.id);
		setEditingText(comment.content);
		setTimeout(() => editInputRef.current?.focus(), 50);
	}, []);

	const cancelEdit = useCallback(() => {
		setEditingCommentId(null);
		setEditingText("");
	}, []);

	const handleSaveEdit = useCallback(async () => {
		if (!editingCommentId || !editingText.trim()) return;
		try {
			const updated = await api.editComment(
				editingCommentId,
				editingText.trim(),
			);
			setComments((prev) =>
				prev.map((c) => (c.id === editingCommentId ? { ...c, ...updated } : c)),
			);
		} catch {
			toast.error("Failed to save edit");
		}
		setEditingCommentId(null);
		setEditingText("");
	}, [editingCommentId, editingText]);

	// Focus canvas on a specific comment (pan + zoom to center it)
	const handleFocusComment = useCallback(
		(comment: CanvasCommentRow) => {
			const container = canvasRef.current;
			if (!container) return;
			const rect = container.getBoundingClientRect();
			const viewW = rect.width;
			const viewH = rect.height;
			const targetZoom = Math.max(zoom, 0.8);
			const offsetX = viewW / 2 - comment.posX * targetZoom;
			const offsetY = viewH / 2 - comment.posY * targetZoom;
			setZoom(targetZoom);
			setCanvasOffset({ x: offsetX, y: offsetY });
			setActiveCommentId(comment.id);
			setPendingComment(null);
		},
		[zoom],
	);

	// --- Load existing comments + WebSocket for live updates ---
	useEffect(() => {
		if (!projectId) return;

		// 1. Load existing comments from the API
		api
			.getComments(projectId)
			.then((rows) => setComments(rows))
			.catch(() => { });

		// 2. Connect WebSocket with auto-reconnect
		const wsUrl = (
			import.meta.env.VITE_API_URL || "http://localhost:4000"
		).replace(/^http/, "ws");

		let ws: WebSocket | null = null;
		let retries = 0;
		let destroyed = false;
		let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

		function connect() {
			if (destroyed) return;
			ws = new WebSocket(`${wsUrl}/ws/canvas/${projectId}`);

			ws.onopen = () => {
				retries = 0; // reset on successful connection
				console.log("[WS] Canvas connected");
			};

			ws.onmessage = (event) => {
				try {
					// The DB trigger sends snake_case (pos_x, user_id, etc.)
					// but CanvasCommentRow uses camelCase — normalize here.
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const raw: any = JSON.parse(event.data);
					const row: CanvasCommentRow = {
						id: raw.id,
						conversationId: raw.conversationId ?? raw.conversation_id,
						userId: raw.userId ?? raw.user_id,
						userName: raw.userName ?? raw.user_name ?? null,
						content: raw.content,
						posX: raw.posX ?? raw.pos_x,
						posY: raw.posY ?? raw.pos_y,
						resolved: raw.resolved,
						createdAt: raw.createdAt ?? raw.created_at,
					};

					setComments((prev) => {
						// If resolved=true arrived via WS (UPDATE from trigger) → remove it immediately
						if (row.resolved) {
							return prev.filter((c) => c.id !== row.id);
						}
						// Deduplicate: skip if already in the list by real id
						if (prev.some((c) => c.id === row.id)) return prev;
						// Replace optimistic draft — use tolerant pos match (DB `real` rounds vs JS float)
						const draftIdx = prev.findIndex(
							(c) =>
								c.id < 0 &&
								c.content === row.content &&
								sameCanvasPos(c.posX, row.posX) &&
								sameCanvasPos(c.posY, row.posY),
						);
						if (draftIdx !== -1) {
							const next = [...prev];
							next[draftIdx] = row;
							return next;
						}
						// Drop any other stray drafts for the same placement (race: WS before post resolves)
						const withoutMatchingDrafts = prev.filter(
							(c) =>
								!(
									c.id < 0 &&
									c.content === row.content &&
									sameCanvasPos(c.posX, row.posX) &&
									sameCanvasPos(c.posY, row.posY)
								),
						);
						return [...withoutMatchingDrafts, row];
					});
				} catch {
					// ignore malformed payloads
				}
			};

			ws.onerror = () => console.warn("[WS] Canvas connection error");

			ws.onclose = () => {
				if (destroyed) return;
				retries++;
				const delay = Math.min(1000 * retries, 5000); // 1s, 2s, 3s… max 5s
				console.warn(
					`[WS] Disconnected. Reconnecting in ${delay}ms (attempt ${retries})`,
				);
				reconnectTimer = setTimeout(connect, delay);
			};
		}

		connect();

		return () => {
			destroyed = true;
			clearTimeout(reconnectTimer);
			ws?.close();
		};
	}, [projectId]);

	return {
		isCommentMode,
		comments,
		pendingComment,
		commentInput,
		setCommentInput,
		activeCommentId,
		hoveredCommentId,
		setHoveredCommentId,
		editingCommentId,
		editingText,
		setEditingText,
		commentInputRef,
		editInputRef,
		handleCommentToggle,
		placePendingComment,
		cancelPendingComment,
		handleCommentSubmit,
		handleDeleteComment,
		handleResolveComment,
		handleMarkerClick,
		handleStartEdit,
		cancelEdit,
		handleSaveEdit,
		handleFocusComment,
	};
}
